import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MapboxService, MAP_CREATE_GEO_OPTIONS } from '../mapbox-map/mapbox-map.service';
import { MapStateService } from '../map-state.service';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { MAP_STATE } from '../models';
import { ROUTING_PROFILE } from '../services/routing.service';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { DrawRoutingService } from '../services/draw-routing.service';
import { TranslateService } from '../../core/services';
import { DialogService } from '../../shared/services/dialog.service';
import { ConfirmationDialogData } from '../../shared/dialogs';
import { MatOptionModule } from '@angular/material/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';


@Component({
    selector: 'places-map-draw-controls',
    templateUrl: './map-draw-controls.component.html',
    styleUrls: ['./map-draw-controls.component.scss'],
    imports: [
        MatRippleModule, MatTooltipModule, MatFormFieldModule, MatSelectModule, 
        ReactiveFormsModule, FormsModule, MatOptionModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class MapDrawControlsComponent implements OnInit, OnDestroy {
    private _mapService = inject(MapboxService);
    private _mapStateService = inject(MapStateService);
    private _drawRouting = inject(DrawRoutingService);
    private _translate = inject(TranslateService);
    private _dialogService = inject(DialogService);

    public isVisible = false;
    public isDrawVisible = false;

    public state: 'draw' | 'select' | 'create';
    public defaultRoutingProfile: ROUTING_PROFILE;
    public routingProfiles = [
        {
            key: ROUTING_PROFILE.FREE_MODE,
            title: 'Rita på frihand',
        },
        {
            key: ROUTING_PROFILE.DRIVING,
            title: 'Följ väg',
        }
    ];

    private _ngUnsubscribe$ = new Subject<void>();


    constructor() { }

    // tooltips
    public get selectTooltip() {
        return this._translate.instant('Mark sections');
    }

    public get removeTooltip() {
        return this._translate.instant('Remove selected sections');
    }

    public get drawTooltip() {
        return this._translate.instant('Edit line');
    }

    public get undoTooltip() {
        return this._translate.instant('Undo');
    }

    public get createLineTooltip() {
        return this._translate.instant('Create line');
    }

    public get createPointTooltip() {
        return this._translate.instant('Create point');
    }

    public get createAreaTooltip() {
        return this._translate.instant('Create area');
    }

    public get removeFeaturesTooltip() {
        return this._translate.instant('Delete');
    }

    ngOnInit(): void {
        this.defaultRoutingProfile = this._mapService.routingProfile;

        this._mapStateService.getMapPropertyState('state').pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((state: MAP_STATE) => {
            this.isVisible = state === MAP_STATE.EDIT_DRAW_ALTERNATE_ROUTE
                || state === MAP_STATE.EDIT_SELECT_ALTERNATE_ROUTE;

            this.isDrawVisible = state === MAP_STATE.CREATE_FEATURE
                || state === MAP_STATE.EDIT_FEATURE;

            switch (state) {
                    case MAP_STATE.EDIT_DRAW_ALTERNATE_ROUTE:
                case MAP_STATE.EDIT_FEATURE:
                    this.state = 'draw';
                    break;
                case MAP_STATE.EDIT_SELECT_ALTERNATE_ROUTE:
                    this.state = 'select';
                    break;
                case MAP_STATE.CREATE_FEATURE:
                    this.state = 'create';
                    break;
                default:
                    break;
            }
        });
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    //#region controls for the own draw route
    public get removeIsDisabled() {
        return this.state !== 'select';
    }

    public get undoIsDisabled() {
        return this.state !== 'draw' || !this._drawRouting.canUndo();
    }

    public undo() {
        this._drawRouting.undo();
    }

    public changeRoutingProfile(event: MatSelectChange) {
        this._mapService.routingProfile = event.value;
    }

    public removeSelected() {
        this._drawRouting.deleteSelected();
    }

    public setDrawMode() {
        if (this.state === 'draw') {
            return;
        }

        this._mapService.setMapEditState(MAP_STATE.EDIT_DRAW_ALTERNATE_ROUTE);
        this._drawRouting.initDrawMode();
    }

    public async setSelectMode() {
        if (this.state === 'select') {
            return;
        }

        // ask if we want to switch back to the original line
        if (this._drawRouting.hasStartedEdit()) {
            const modalData = new ConfirmationDialogData(
                this._translate.instant('Warning'),
                this._translate.instant('If you continue, you will lose your changes'),
                this._translate.instant('Continue'),
                this._translate.instant('Cancel')
            );
            const dialogResponse = await firstValueFrom(this._dialogService.showConfirmation(modalData));
            if (!dialogResponse) {
                return;
            }
        }

        this._mapService.setMapEditState(MAP_STATE.EDIT_SELECT_ALTERNATE_ROUTE);
        this._drawRouting.resetDraw();
    }
    //#endregion


    //#region controls for the draw
    public async createFeature(type: string) {
        let drawMode: MAP_CREATE_GEO_OPTIONS;
        switch (type) {
            case 'line':
                drawMode = MAP_CREATE_GEO_OPTIONS.LINESTRING;
                break;
            case 'point':
                drawMode = MAP_CREATE_GEO_OPTIONS.POINT;
                break;
            case 'area':
                drawMode = MAP_CREATE_GEO_OPTIONS.POLYGON;
                break;
            default:
                break;
        }

        this._mapService.setCreateFeatureDrawMode(drawMode);
    }

    public get removeFeatureIsDisabled() {
        return !this._mapService.canRemoveEditFeature();
    }

    public removeFeature() {
        this._mapService.removeEditFeature();
    }

    public get drawFeatureIsDisabled() {
        return this._mapService.canRemoveEditFeature();
    }

    //#endregion
}
